import { fmtDate } from "utils/date";
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { BuzonDudasService } from "service/BuzonDudasService";
import ModalRespuestaDuda from "components/Modals/ModalRespuestaDuda";
import FSelect from "components/Select/FSelect";
import FDateInput from "components/DateInput/FDateInput";
import CustomPagination from "components/Pagination/Pagination";

const buzonService = new BuzonDudasService();

function BuzonDudas() {
  const today = new Date().toISOString().split("T")[0];

  const notificationAlertRef = useRef(null);
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [listPreguntas, setListPreguntas] = useState([]);
  const [listFiltered, setListFiltered] = useState([]);
  const [fechaFiltro, setFechaFiltro] = useState(today);
  const [filterEstado, setFilterEstado] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPregunta, setSelectedPregunta] = useState(null);

  // Loading state
  const [loadingBuscar, setLoadingBuscar] = useState(false);

  // Confirm modal states
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ mensaje: "", onConfirm: null });

  const getPreguntas = async () => {
    setLoadingBuscar(true);
    try {
      const data = await buzonService.getPreguntasByFecha(fechaFiltro);
      const list = Array.isArray(data) ? data : data?.data || [];
      setListPreguntas(list);
      applyFilters(list, searchQuery, filterEstado);
    } catch (error) {
      console.error("Error cargando preguntas:", error);
      notify("danger", "Error al cargar las preguntas");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const applyFilters = (data, query, estado) => {
    let filtered = data;
    if (estado !== "Todos") {
      filtered = filtered.filter((item) => item.estado === estado);
    }
    if (query) {
      const q = query.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.pregunta || "").toLowerCase().includes(q)
      );
    }
    setListFiltered(filtered);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(listPreguntas, query, filterEstado);
    setCurrentPage(1);
  };

  const handleFilterEstado = (e) => {
    const estado = e.target.value;
    setFilterEstado(estado);
    applyFilters(listPreguntas, searchQuery, estado);
    setCurrentPage(1);
  };

  const handleFecha = (e) => {
    setFechaFiltro(e.target.value);
    setCurrentPage(1);
  };

  const handleResponder = (pregunta) => {
    setSelectedPregunta(pregunta);
    setModalOpen(true);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setSelectedPregunta(null);
  };

  const handleDelete = async (pregunta) => {
    setConfirmData({
      mensaje: "¿Estás seguro de eliminar esta pregunta? Esta acción no se puede deshacer.",
      onConfirm: async () => {
        setConfirmModal(false);
        try {
          await buzonService.deletePregunta(pregunta._id);
          getPreguntas();
        } catch (error) {
          console.error("Error eliminando pregunta:", error);
          notify("danger", "Error al eliminar la pregunta");
        }
      },
    });
    setConfirmModal(true);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = listFiltered.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Buzón de Dudas</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button color="link" className="btn-icon btn-round">
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 2: Barra de filtros */}
                <Row className="align-items-end mb-4">
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Label for="fecha">Fecha</Label>
                      <FDateInput
                        id="fecha"
                        value={fechaFiltro}
                        onChange={handleFecha}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Label for="estado">Estado</Label>
                      <FSelect
                        id="estado"
                        value={filterEstado}
                        onChange={handleFilterEstado}
                        options={[
                          { value: "Todos", label: "Todos" },
                          { value: "Registrado", label: "Registrado" },
                          { value: "Respondido", label: "Respondido" },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="5">
                    <FormGroup className="mb-0">
                      <Label for="search">Búsqueda por Contenido</Label>
                      <Input
                        type="text"
                        id="search"
                        placeholder="Buscar en preguntas..."
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ height: "40px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={getPreguntas}
                        disabled={loadingBuscar}
                        style={{ height: "40px", padding: "0" }}
                        title="Buscar"
                      >
                        {loadingBuscar ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones (Espacio reservado) */}
                <Row className="mb-4">
                  <Col md="12" className="text-right">
                    {/* Espacio para acciones adicionales si fueran necesarias */}
                  </Col>
                </Row>

                <hr className="my-4" />

                <Table responsive>

                  <thead className="text-primary">
                    <tr>
                      <th>Fecha</th>
                      <th>Pregunta</th>
                      <th>Alumno</th>
                      <th>Curso</th>
                      <th>Estado</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((row, index) => (
                      <tr
                        key={row._id || index}
                        style={
                          row.estado === "Respondido"
                            ? { opacity: 0.7 }
                            : {}
                        }
                      >
                        <td>
                          {row.fecha
                            ? fmtDate(row.fecha)
                            : "-"}
                        </td>
                        <td>{row.pregunta}</td>
                        <td>
                          {row.alumno
                            ? `${row.alumno.nombres || ""} ${row.alumno.apellidopaterno || ""}`
                            : "-"}
                        </td>
                        <td>{row.curso?.denominacion || "-"}</td>
                        <td>
                          <Badge
                            color={
                              row.estado === "Respondido" ? "success" : "warning"
                            }
                          >
                            {row.estado}
                          </Badge>
                        </td>
                        <td className="text-right">
                          <Button
                            color="primary"
                            size="sm"
                            className="btn-icon btn-link"
                            title="Responder"
                            onClick={() => handleResponder(row)}
                          >
                            <i className="tim-icons icon-chat-33" />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-icon btn-link"
                            title="Eliminar"
                            onClick={() => handleDelete(row)}
                          >
                            <i className="tim-icons icon-trash-simple" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {listFiltered.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No se encontraron preguntas para esta fecha
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <CustomPagination
                  itemsPerPage={itemsPerPage}
                  totalItems={listFiltered.length}
                  paginate={setCurrentPage}
                  currentPage={currentPage}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <ModalRespuestaDuda
        isOpen={modalOpen}
        toggle={toggleModal}
        selectedPregunta={selectedPregunta}
        onSave={getPreguntas}
      />

      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} size="sm" centered>
        <ModalHeader toggle={() => setConfirmModal(false)}>Confirmar acción</ModalHeader>
        <ModalBody><p>{confirmData.mensaje}</p></ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancelar</Button>
          <Button color="danger" onClick={confirmData.onConfirm}>Confirmar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default BuzonDudas;
